/**
 * BotSpot Web Dashboard - Persona Gallery Component
 *
 * Displays a grid of hatched agent persona cards with real-time updates.
 */

import React, { useState, useEffect } from "react";
import { useHatchery } from "../hooks/useHatchery";
import { PersonaCard } from "./PersonaCard";
import { getErrorMessage } from "../api/client";
import type { AgentIdentityPublic } from "../api/types";
import { PIXEL, MONO } from "../theme";

/**
 * Persona Gallery Component
 *
 * Fetches and displays all hatched agents with persona profiles.
 * Polls every 10 seconds for data consistency.
 */
export function PersonaGallery() {
  const { agents: initialAgents, loading, error, refetch } = useHatchery(50, 10000);
  const [agents, setAgents] = useState<AgentIdentityPublic[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);

  // Update local state when initial agents change
  useEffect(() => {
    setAgents(initialAgents);
  }, [initialAgents]);

  /**
   * Handle view agent details
   */
  const handleView = (agentId: string) => {
    // Navigation is handled within PersonaCard component
  };

  /**
   * Handle hatch similar agent
   */
  const handleHatch = async (agentId: string) => {
    setActionError(null);
    try {
      // TODO: Implement hatch similar functionality
      // For now, just refresh the gallery
      await refetch();
    } catch (err) {
      setActionError(`Failed to hatch similar agent: ${getErrorMessage(err)}`);
    }
  };

  /**
   * Render loading state
   */
  if (loading && agents.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}>🔄</div>
          <p style={styles.loadingText}>Loading persona gallery...</p>
        </div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error && agents.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>❌</div>
          <h3 style={styles.errorTitle}>Failed to load persona gallery</h3>
          <p style={styles.errorMessage}>{error}</p>
          <button style={styles.retryButton} onClick={refetch}>
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render empty state
   */
  if (agents.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyContainer}>
          <div style={styles.emptyIcon}>🥚</div>
          <h3 style={styles.emptyTitle}>No hatched agents yet</h3>
          <p style={styles.emptyMessage}>
            The gallery is empty. Be the first to hatch an agent!
          </p>
          <button style={styles.createButton}>
            🥚 Hatch Your First Agent
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render persona gallery
   */
  return (
    <div style={styles.container}>
      {/* Action Error Banner */}
      {actionError && (
        <div style={styles.actionErrorBanner}>
          <span style={styles.actionErrorIcon}>⚠️</span>
          <span style={styles.actionErrorText}>{actionError}</span>
          <button
            style={styles.dismissButton}
            onClick={() => setActionError(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Gallery Header */}
      <div style={styles.galleryHeader}>
        <div>
          <h2 style={styles.galleryTitle}>Persona Gallery</h2>
          <p style={styles.gallerySubtitle}>
            {agents.length} hatched agent{agents.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button style={styles.refreshButton} onClick={refetch}>
          🔄 Refresh
        </button>
      </div>

      {/* Persona Grid */}
      <div style={styles.grid}>
        {agents.map((agent) => (
          <div key={agent.agent_id} style={styles.gridItem}>
            <PersonaCard
              agent={agent}
              onView={handleView}
              onHatch={handleHatch}
            />
          </div>
        ))}
      </div>

      {/* Loading Indicator (when polling) */}
      {loading && agents.length > 0 && (
        <div style={styles.pollingIndicator}>
          <span style={styles.pollingIcon}>🔄</span>
          <span style={styles.pollingText}>Updating...</span>
        </div>
      )}
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "4rem 2rem",
  },
  spinner: {
    fontSize: "3rem",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    marginTop: "1rem",
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.5)",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "4rem 2rem",
    textAlign: "center",
  },
  errorIcon: {
    fontSize: "3rem",
    marginBottom: "1rem",
  },
  errorTitle: {
    margin: "0 0 0.5rem 0",
    fontFamily: PIXEL,
    fontSize: "0.7rem",
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.87)",
    letterSpacing: "0.06em",
  },
  errorMessage: {
    margin: "0 0 1.5rem 0",
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.5)",
    maxWidth: "500px",
  },
  retryButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "transparent",
    border: "2px solid #006AA7",
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    color: "#006AA7",
    letterSpacing: "0.1em",
    boxShadow: "0 0 8px rgba(0,106,167,0.2)",
    transition: "background-color 0.2s",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "4rem 2rem",
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: "4rem",
    marginBottom: "1rem",
  },
  emptyTitle: {
    margin: "0 0 0.5rem 0",
    fontFamily: PIXEL,
    fontSize: "0.7rem",
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.87)",
    letterSpacing: "0.06em",
  },
  emptyMessage: {
    margin: "0 0 1.5rem 0",
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.5)",
    maxWidth: "400px",
  },
  createButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "#FECC00",
    border: "none",
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    color: "#000",
    letterSpacing: "0.1em",
    boxShadow: "0 0 12px rgba(254,204,0,0.25)",
    transition: "background-color 0.2s",
  },
  actionErrorBanner: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    padding: "1rem",
    marginBottom: "1.5rem",
  },
  actionErrorIcon: {
    fontSize: "1.25rem",
  },
  actionErrorText: {
    flex: 1,
    fontFamily: MONO,
    fontSize: "0.95rem",
    color: "#fca5a5",
  },
  dismissButton: {
    padding: "0.25rem 0.5rem",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.6)",
    transition: "color 0.2s",
  },
  galleryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1.5rem",
  },
  galleryTitle: {
    margin: 0,
    fontFamily: PIXEL,
    fontSize: "0.8rem",
    fontWeight: 400,
    color: "#fff",
    letterSpacing: "0.06em",
    textShadow: "0 0 12px rgba(0,106,167,0.4)",
    marginBottom: "0.25rem",
  },
  gallerySubtitle: {
    margin: 0,
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.4)",
  },
  refreshButton: {
    padding: "0.5rem 1rem",
    backgroundColor: "transparent",
    border: "1px solid rgba(0,106,167,0.3)",
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    color: "rgba(255,255,255,0.5)",
    letterSpacing: "0.08em",
    transition: "background-color 0.2s",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
    gap: "1.5rem",
  },
  gridItem: {
    transition: "opacity 0.2s",
  },
  pollingIndicator: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    marginTop: "1.5rem",
    padding: "0.75rem",
    fontFamily: MONO,
    fontSize: "0.875rem",
    color: "rgba(255, 255, 255, 0.5)",
  },
  pollingIcon: {
    fontSize: "1rem",
    animation: "spin 1s linear infinite",
  },
  pollingText: {
    fontFamily: MONO,
    lineHeight: 1,
  },
};
